/** Formatting and display mapping only. Amounts and balances come from the API. */
export const KIND_LABELS = { expense: 'مصروف ورشة', payroll: 'مصروف ورشة', income: 'إيراد ورشة' };
export const PAYMENT_LABELS = { cash: 'نقدي', bank: 'بنكي', pos: 'شبكة', credit: 'آجل' };
export const STATUS_LABELS = { posted: 'مرحّل', reversed: 'معكوس', paid: 'مسددة', partial: 'جزئية', unpaid: 'غير مسددة' };
export const ORIGIN_LABELS = { journal_manual: 'تسجيل يدوي', payroll_manual: 'رواتب', smart_pos: 'نقطة بيع', visit_finalization: 'اعتماد زيارة', visit_payment: 'دفعة زيارة', invoice: 'فاتورة', supplier: 'مورد', pos: 'نقطة بيع (قديم)', adjustment: 'تسوية', reversal:'عكس قيد', document_settlement:'سداد حركة' };

/** @param {unknown} value @returns {string} */
export const textValue = value => typeof value === 'string' ? value.trim() : '';
/** @param {unknown} value @returns {string} */
export function money(value) {
  // Decimal formatting, without deriving a ledger amount from other fields or
  // converting a large database decimal into an imprecise JavaScript number.
  const match = String(value ?? '').match(/^(-?)(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return 'غير متاح';
  const whole = match[2].replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${match[1]}${whole}.${(match[3] || '').padEnd(2, '0')} ر.س`;
}
/** @param {string | null | undefined} date @returns {string} */
export function dateLabel(date) {
  if (!date || !/^\d{4}-\d{2}-\d{2}/.test(date)) return 'التاريخ غير مسجل';
  const value = new Date(`${date.slice(0, 10)}T12:00:00Z`);
  if (Number.isNaN(value.getTime())) return 'التاريخ غير مسجل';
  return value.toLocaleDateString('ar-SA-u-ca-gregory', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Riyadh' });
}
/** @param {string | null | undefined} timestamp @returns {string} */
export function timeLabel(timestamp) {
  if (!timestamp || !/T\d{2}:\d{2}/.test(timestamp)) return '';
  const value = new Date(timestamp);
  return Number.isNaN(value.getTime()) ? '' : value.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Riyadh' });
}
/** @param {string | null | undefined} timestamp @returns {string} */
export function timestampDay(timestamp) {
  if (!timestamp || !/T\d{2}:\d{2}/.test(timestamp)) return '';
  const value = new Date(timestamp);
  if (Number.isNaN(value.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {timeZone:'Asia/Riyadh',year:'numeric',month:'2-digit',day:'2-digit'}).formatToParts(value);
  /** @param {string} name */
  const part = name => parts.find(item=>item.type===name)?.value || '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}
/** @param {import('./workshopFinanceTypes').FinanceVehicle | null | undefined} vehicle @returns {string} */
export function vehicleLabel(vehicle) {
  if (!vehicle?.id) return 'بيانات المركبة غير متاحة';
  const name = [textValue(vehicle.brand), textValue(vehicle.model)].filter(Boolean).join(' ');
  const plate = textValue(vehicle.plate_number);
  return [name, plate].filter(Boolean).join(' · ') || 'مركبة مرتبطة';
}
/** @param {import('./workshopFinanceTypes').FinanceLinkage} document @returns {import('./workshopFinanceTypes').LinkagePresentation} */
export function linkage(document) {
  // Explicit source linkage wins; a stray customer relation must never turn an
  // instant movement into a customer movement.
  if (document.link_type === 'instant') return { label: 'فوري', detail: '', href: null };
  if (document.link_type === 'vehicle') return { label: 'المركبة', detail: vehicleLabel(document.vehicle), href: document.vehicle?.id ? `/vehicle/${encodeURIComponent(document.vehicle.id)}` : null };
  if (document.link_type === 'customer') return { label: 'العميل', detail: textValue(document.customer?.name) || 'اسم العميل غير متاح', href: document.customer?.id ? `/customer/${encodeURIComponent(document.customer.id)}` : null };
  return { label: 'الربط', detail: 'غير مسجل', href: null };
}
/** @template {import('./workshopFinanceTypes').FinanceIdentity} T
 * @param {T[] | null | undefined} items @returns {T[]} */
export function uniqueDocuments(items) {
  const ids = new Set();
  return (Array.isArray(items) ? items : []).filter(item => {
    if (!textValue(item?.id) || !KIND_LABELS[item.kind] || ids.has(item.id)) return false;
    ids.add(item.id); return true;
  });
}
export function requestId() {
  if (typeof window.crypto?.randomUUID === 'function') return window.crypto.randomUUID();
  if (typeof window.crypto?.getRandomValues !== 'function') throw new Error('تعذر تجهيز مرجع آمن. افتح الموقع عبر HTTPS.');
  const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
}
/** @param {unknown} error @returns {string} */
export function requestError(error) {
  const response = error && typeof error === 'object' && 'response' in error ? error.response : null;
  const data = response && typeof response === 'object' && 'data' in response ? response.data : null;
  const detail = data && typeof data === 'object' && 'detail' in data ? data.detail : null;
  return typeof detail === 'string' && /[\u0600-\u06ff]/.test(detail) ? detail : 'تعذر تنفيذ الطلب. أعد المحاولة بالمرجع نفسه.';
}
